import React from 'react';
import moment from 'moment';

const stripHtml = (html) => (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const BusinessTab = ({ installer, logoUrl, logoUploading, logoRemoving, onLogoUpload, onLogoRemove, onOpenSettingsModal, onOpenWaiverModal, getTimeTrackingLabel }) => {
  const waiverPreview = stripHtml(installer?.waiver_html);
  const waiverUpdatedAt = installer?.waiver_updated_at;

  return (
    <>
      {/* Company Information */}
      <div className="card mb-4">
        <div className="card-header">
          <h4 className="mb-0">Company Information</h4>
        </div>
        <div className="card-body">
          {installer && (
            <div className="row">
              <div className="col-md-4 mb-3">
                <strong>Business Name:</strong><br />
                {installer.Installer_Name || 'Not set'}
              </div>
              <div className="col-md-4 mb-3">
                <strong>Email:</strong><br />
                {installer.installer_email || installer.Email || 'Not set'}
              </div>
              <div className="col-md-4 mb-3">
                <strong>Phone:</strong><br />
                {installer.installer_phone_number || 'Not set'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Company Logo */}
      <div className="card mb-4">
        <div className="card-header">
          <h4 className="mb-0">
            <i className="fa-sharp fa-regular fa-image mr-2"></i>
            Company Logo
          </h4>
        </div>
        <div className="card-body">
          <p className="text-muted mb-3">
            Upload your company logo to replace the default Jobvious logo in the sidebar navigation.
          </p>
          <div className="d-flex align-items-center">
            {logoUrl ? (
              <div className="mr-4">
                <div style={{ background: '#f26c35', borderRadius: '8px', padding: '12px 16px', display: 'inline-block' }}>
                  <img
                    src={logoUrl}
                    alt="Company Logo"
                    style={{ maxWidth: '200px', maxHeight: '60px', objectFit: 'contain' }}
                  />
                </div>
              </div>
            ) : (
              <div className="mr-4 text-muted" style={{ minWidth: '200px' }}>
                <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                  <i className="fa-sharp fa-regular fa-image fa-2x mb-2 d-block" style={{ opacity: 0.4 }}></i>
                  <small>No logo uploaded</small>
                </div>
              </div>
            )}
            <div>
              <label className="btn btn-outline-primary btn-sm mb-2" style={{ cursor: 'pointer' }}>
                <i className="fa-sharp fa-regular fa-upload mr-1"></i>
                {logoUploading ? 'Uploading...' : (logoUrl ? 'Change Logo' : 'Upload Logo')}
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/svg+xml"
                  onChange={onLogoUpload}
                  disabled={logoUploading || logoRemoving}
                  style={{ display: 'none' }}
                />
              </label>
              {logoUrl && (
                <div>
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={onLogoRemove}
                    disabled={logoUploading || logoRemoving}
                  >
                    <i className="fa-sharp fa-regular fa-trash mr-1"></i>
                    {logoRemoving ? 'Removing...' : 'Remove Logo'}
                  </button>
                </div>
              )}
              <small className="text-muted d-block mt-2">PNG, JPEG, or SVG. Max 2MB.</small>
            </div>
          </div>
        </div>
      </div>

      {/* Company Settings */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Company Settings</h4>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={onOpenSettingsModal}
          >
            <i className="fa-sharp fa-regular fa-edit mr-1"></i> Edit
          </button>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <strong>Materials Tracking:</strong><br />
              <span className={`badge ${installer?.materials_tracking_enabled !== false ? 'badge-success' : 'badge-secondary'}`}>
                {installer?.materials_tracking_enabled !== false ? 'Enabled' : 'Disabled'}
              </span>
              <p className="text-muted small mt-1 mb-0">
                {installer?.materials_tracking_enabled !== false
                  ? 'Technicians will be prompted to record materials used when finishing services'
                  : 'Materials collection is disabled'}
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Time Tracking:</strong><br />
              <span className={`badge ${(installer?.time_tracking_mode && installer.time_tracking_mode !== 'none') ? 'badge-success' : 'badge-secondary'}`}>
                {getTimeTrackingLabel(installer?.time_tracking_mode)}
              </span>
              <p className="text-muted small mt-1 mb-0">
                {installer?.time_tracking_mode === 'required'
                  ? 'Staff must check in before starting work on jobs'
                  : installer?.time_tracking_mode === 'voluntary'
                  ? 'Staff can optionally check in/out to track hours'
                  : 'Time tracking features are hidden'}
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Cash Discount:</strong><br />
              <span className={`badge ${installer?.waive_platform_fee_offline ? 'badge-success' : 'badge-secondary'}`}>
                {installer?.waive_platform_fee_offline ? 'Enabled' : 'Disabled'}
              </span>
              <p className="text-muted small mt-1 mb-0">
                5% discount on catalog prices for cash/check payments
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Service Waiver */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="mb-0">
            <i className="fa-sharp fa-regular fa-file-signature mr-2"></i>
            Customer Work Agreement
          </h4>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={onOpenWaiverModal}
          >
            <i className="fa-sharp fa-regular fa-edit mr-1"></i>
            {waiverPreview ? 'Edit' : 'Create'}
          </button>
        </div>
        <div className="card-body">
          <p className="text-muted small mb-3">
            Shown to customers on the signature step at retail check-in. Replaces the default agreement language.
          </p>
          {waiverPreview ? (
            <>
              <div
                className="border rounded p-3 mb-2 bg-light text-truncate"
                style={{ maxWidth: '100%' }}
                title={waiverPreview}
              >
                {waiverPreview.length > 200 ? `${waiverPreview.slice(0, 200)}…` : waiverPreview}
              </div>
              {waiverUpdatedAt && (
                <small className="text-muted">
                  Last updated {moment(waiverUpdatedAt).format('MMM D, YYYY [at] h:mm A')}
                </small>
              )}
            </>
          ) : (
            <div className="text-muted">
              <em>Not configured — default agreement language will be shown at check-in.</em>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BusinessTab;
